/**
 * Animation clip ingestion for the character rig.
 *
 * The avatar IS the UE5 "Manny" skeleton (see mannyRig.ts / avatar.ts), so clips
 * authored on Manny bind NATIVELY — no retargeting. `normalizeClip()` is the one
 * ingestion boundary: it renames each `<bone>.quaternion` track to the
 * `.bones[<bone>].quaternion` path the mixer binds against, and DROPS position +
 * scale tracks (the sim owns translation). It also produces a `report` +
 * `violations[]` so the test suite can gate every registry clip generically.
 */
import * as THREE from "three";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { buildRetargeter, canonName, MIXAMO_TO_MANNY, UAL_TO_MANNY, type RetargetMap } from "./retarget";
import { MALE_RIG } from "./maleRig";
import { closeLocomotionLoop, smoothLocomotionTorso } from './loopSeam';
// Re-exported so clip authors can `retarget: MIXAMO_TO_MANNY` from the registry below.
export { MIXAMO_TO_MANNY };

/** Forward-axis reconciliation: the avatar mounts the rig under this yaw so the
 *  rig's rest forward maps to our -Z forward. ONE source of truth. */
export const RIG_YAW = Math.PI;

/** Scratch for the hip-drift vector (avoids a per-clip allocation in normalizeClip). */
const _drift = new THREE.Vector3();

/** The frame a `.bones[pelvis].position` track binds against, realized from MALE_RIG once:
 *  the ROOT bone's rest rotation (values are root-LOCAL; the root never animates — clips drop
 *  its track), the pelvis bind position, and the pelvis rest WORLD height (for scaling a
 *  foreign source's hip journey by the hip-height ratio). */
let _malePelvisFrame: { rootQ: THREE.Quaternion; invRootQ: THREE.Quaternion; bind: THREE.Vector3; hipY: number } | null = null;
function malePelvisFrame() {
  if (_malePelvisFrame) return _malePelvisFrame;
  const root = MALE_RIG[0];
  const pelvis = MALE_RIG.find((b) => b[0] === "pelvis")!;
  const rootQ = new THREE.Quaternion(root[5], root[6], root[7], root[8]);
  const bind = new THREE.Vector3(pelvis[2], pelvis[3], pelvis[4]);
  const world = bind.clone().applyQuaternion(rootQ).add(new THREE.Vector3(root[2], root[3], root[4]));
  _malePelvisFrame = { rootQ, invRootQ: rootQ.clone().invert(), bind, hipY: world.y };
  return _malePelvisFrame;
}

/** Native (Manny-authored) clips bind by exact bone name. The Quaternius UAL packs
 *  name the head bone "Head" while our rig (maleRig.ts) uses lowercase "head"; every
 *  other bone already matches. Alias so the head track binds instead of silently
 *  riding the rest pose. Applied ONLY on the native path (retarget targets are
 *  already correct-case Manny names). */
const NATIVE_BONE_ALIAS: Record<string, string> = { Head: "head" };

export type ClipKey = string;
export type AvatarClips = Partial<Record<string, THREE.AnimationClip>>;
/** Direction a locomotion clip travels (for the ingestion contract). */
export type ClipDir = "fwd" | "back" | "lateral" | "none";

export interface ClipDef {
  /** Unique id, also the mixer action name. */
  key: string;
  /** /anim/... path (lowercase — production is case-sensitive). */
  file: string;
  /** Contract: which direction this locomotion clip should travel. */
  dir?: ClipDir;
  /** Should loop seamlessly (default true; one-shots set `once`). */
  loop?: boolean;
  /** One-shot (LoopOnce + clamp) — jump/land; default loops. */
  once?: boolean;
  /** UPPER-BODY LAYER: strip the pelvis + leg tracks at load so this clip only drives
   *  the spine/arms/head. Played over locomotion at high weight, it owns the torso while
   *  the legs stay 100% locomotion (THREE normalizes per-bone). Used for unarmed combat. */
  upperOnly?: boolean;
  /** LOWER-BODY clip: keep ONLY the pelvis + leg tracks (drop spine/arms/head). The unarmed
   *  fighting-stance legs (the bladed pelvis the torso clip sits on) come from this. */
  lowerOnly?: boolean;
  /** FOREIGN skeleton: retarget this clip's bones onto Manny on ingest (e.g. MIXAMO_TO_MANNY).
   *  Absent = the clip is already authored on Manny and binds natively (no retarget). */
  retarget?: RetargetMap;
  /** KEEP the pelvis .position track (normally position is dropped — the sim owns translation).
   *  For clips whose whole point is an authored VERTICAL journey (the dive landing: fly in ~1m up,
   *  drop through a deep crouch, stand), rotations alone leave the body floating at bind height with
   *  the feet tucked in the air. Values are re-expressed as a DELTA off the clip's own rest pose and
   *  applied about the rig's pelvis bind — the source pack and the runtime rig decompose the hip
   *  height differently (the rig buries a foot-offset in its root), so raw values must never bind
   *  directly. Under `retarget`, the source's hip delta additionally rotates through the source
   *  root frame + align and scales by the HIP-HEIGHT RATIO (a 0.92m-hip source crouching to its
   *  floor crouches OUR hips to OUR floor) — used by the UAL preview so crouch/slide/lie/climb
   *  clips don't play with the pelvis frozen at standing height (limbs curling around a pinned
   *  body: the "contorted / shrunken" preview look). */
  keepHipsPos?: boolean;
  /** Native UE centimetre locomotion: retain cyclic hip sway/bounce around the
   * metric bind, without carrying root travel or a source-specific hip offset. */
  cyclicHips?: boolean;
  /** Match both pose and motion across a nearly cyclic source take's seam. */
  closeLoop?: boolean;
}

/** Pelvis + leg bones (incl. twists, foot IK) — dropped from `upperOnly` clips so the
 *  combat layer never touches the legs (locomotion keeps driving them). */
const LOWER_BODY = /^(pelvis|thigh_|calf_|foot_|ball_|ik_foot)/;

/** What `normalizeClip` measured/decided about a clip — drives test contracts. */
export interface ClipReport {
  animatedBones: number;
  /** Net root XZ travel in the source before position was dropped. */
  rootDriftX: number;
  rootDriftZ: number;
  /** Max per-bone quaternion angle between the first and last frame (loop seam). */
  loopErrRad: number;
}

export interface NormalizedClip {
  key: string;
  clip: THREE.AnimationClip;
  report: ClipReport;
  violations: string[];
}

const MIN_ANIMATED_BONES = 18;
// ~63°: UE motion-matching loops aren't authored as perfect hard-loops (they're
// meant to blend), so a modest first-vs-last seam is normal; this catches only
// grossly broken loops.
const LOOP_ERR_MAX = 1.1;

// Manny clip registry. GROUND + AIM clips live in /anim/manny/ (import the Manny
// set here — keys cover what selectLocomotion expects); the jetpack FLIGHT clips
// live in /anim/flight/. Absent files just fail to load and the state machine
// falls back via has() — so the registry can list clips before they're imported.
/** The 8 strafe directions used by the armed (aim-stance) blendspace, at 45°
 *  increments from forward (CCW order for the angular blend). */
export const DIRS8 = ["f", "fr", "r", "br", "b", "bl", "l", "fl"] as const;

/** Full directional aim-stance set for one weapon prefix ("p"=pistol, "r"=rifle):
 *  idle + 8-way walk + 8-way run + sprint/jump/fall/land/crouch. */
function aimDefs(p: "p" | "r"): ClipDef[] {
  const out: ClipDef[] = [
    { key: `${p}_idle`, file: `/anim/manny/${p}_idle.glb`, dir: "none" },
    { key: `${p}_sprint`, file: `/anim/manny/${p}_sprint.glb`, dir: "fwd" },
    { key: `${p}_jump`, file: `/anim/manny/${p}_jump.glb`, once: true },
    // NOTE: the per-stance falling clip is intentionally REMOVED — its spread-eagle
    // retarget read as a "leg splay" during flight transitions. Airborne uses the leap pose.
    { key: `${p}_land`, file: `/anim/manny/${p}_land.glb`, once: true },
    { key: `${p}_crouch_idle`, file: `/anim/manny/${p}_crouch_idle.glb`, dir: "none" },
    { key: `${p}_crouch_walk`, file: `/anim/manny/${p}_crouch_walk.glb` },
  ];
  // FALL loop — the RIFLE ships one (GASP Jump_Loop_Fall_Rifle, native Manny, authored with the
  // weapon in hand). The generic `fall` key stays retired: that was a spread-eagle retarget that
  // read as a leg splay. Without this the whole airborne arc HELD the jump pose.
  if (p === "r") out.push({ key: "r_fall", file: "/anim/manny/r_fall.glb" });
  // 8-way directional blendspaces: walk + run (forward stance) AND crouch (the Crouch_Loop_* set).
  for (const g of ["walk", "run", "crouch"] as const) {
    for (const d of DIRS8) out.push({ key: `${p}_${g}_${d}`, file: `/anim/manny/${p}_${g}_${d}.glb` });
  }
  return out;
}

/** SWORD (melee) armed-stance set — mirrors the gun aimDefs shape, but only the clips the pack ships:
 *  idle + 8-way walk + 8-way run + the single-press basic attack (NormalAttack). No sprint/jump/land/
 *  crouch exist for the sword → those fall back via has() (sprint→run_f, air→the universal jump/fall,
 *  crouch→idle). Prefix "sd" — NOT "sw", which is the SWIM set. UE5 Manny FBX (no retarget); the _RM
 *  root tracks are dropped by canonicalize → in-place. Deployed by test/deploy-sword.mjs. */
function swordDefs(): ClipDef[] {
  const out: ClipDef[] = [
    { key: "sd_idle", file: "/anim/manny/sd_idle.glb", dir: "none" },
    // Forward RUN (sprint slot) reuses the UNARMED sprint clip — the SSH_Run_02 looped with a left-foot
    // seam lag; rather than butcher it, share the clean unarmed sprint (same file as the `sprint` key).
    { key: "sd_sprint", file: "/anim/manny/sprint.glb", dir: "fwd" },
    // LMB COMBO CHAIN — consecutive swings advance sd_attack → sd_attack2 → sd_attack3 (SSH Attack_01/02/
    // 03) within a window. The chain order and every window (cancel/grace) are AUTHORED IN REAL MS in
    // shared/src/combatActions.ts (SWORD_COMBO_CHAIN + COMBAT_ACTIONS), which the server reads too;
    // avatar.ts swordSwing() just plays them. Full-body one-shots. See deploy-sword.mjs.
    { key: "sd_attack", file: "/anim/manny/sd_attack.glb", once: true },
    { key: "sd_attack2", file: "/anim/manny/sd_attack2.glb", once: true },
    { key: "sd_attack3", file: "/anim/manny/sd_attack3.glb", once: true },
    // UAL LIGHT STRING A→D — the 4-hit combo (Quaternius UAL2, deployed by convert-ual.mts,
    // RETARGETED OFFLINE onto MALE_RIG bone names — binds natively, like the SSH clips).
    // ATTACK-ONLY clips (user call 2026-07-23): the _Rec halves are SEPARATE combo-END
    // recoveries — chaining skips them; a stopped chain plays sd_lightN_rec (stepAction).
    // Authored at game speed: the table drives them at rate 1.0, no 2.2× ladder branch.
    { key: "sd_light1", file: "/anim/ual/sd_light1.glb", once: true },
    { key: "sd_light1_rec", file: "/anim/ual/sd_light1_rec.glb", once: true },
    { key: "sd_light2", file: "/anim/ual/sd_light2.glb", once: true },
    { key: "sd_light2_rec", file: "/anim/ual/sd_light2_rec.glb", once: true },
    { key: "sd_light3", file: "/anim/ual/sd_light3.glb", once: true },
    { key: "sd_light3_rec", file: "/anim/ual/sd_light3_rec.glb", once: true },
    { key: "sd_light4", file: "/anim/ual/sd_light4.glb", once: true }, // Light_D — the finisher

    // VICTIM HIT FLINCHES (UAL1, directional; same offline-retargeted deploy) — the feedback
    // stack's victim half: played by avatar.flinch(dir) when a hit lands on us un-guarded
    // (main.ts incoming-damage path). hit_b is the pack's head-snap (no back-hit ships).
    { key: "hit_f", file: "/anim/ual/hit_f.glb", once: true },
    { key: "hit_b", file: "/anim/ual/hit_b.glb", once: true },
    { key: "hit_l", file: "/anim/ual/hit_l.glb", once: true },
    { key: "hit_r", file: "/anim/ual/hit_r.glb", once: true },
    // NPC LIFE EMOTES (2026-07-31, UAL1/2 via convert-ual): activity idles the
    // regionNpcLife emote channel plays on stationary residents — shopkeepers
    // serve counters, farmers tend rows, taverns talk and drink. ALL LOOP
    // (repetition reads as ambience; no clamp management).
    { key: "emo_counter_idle", file: "/anim/ual/emo_counter_idle.glb" },
    { key: "emo_counter_give", file: "/anim/ual/emo_counter_give.glb" },
    { key: "emo_counter_show", file: "/anim/ual/emo_counter_show.glb" },
    { key: "emo_talk", file: "/anim/ual/emo_talk.glb" },
    { key: "emo_lookaround", file: "/anim/ual/emo_lookaround.glb" },
    { key: "emo_tired", file: "/anim/ual/emo_tired.glb" },
    { key: "emo_foldarms", file: "/anim/ual/emo_foldarms.glb" },
    { key: "emo_drink", file: "/anim/ual/emo_drink.glb" },
    { key: "emo_consume", file: "/anim/ual/emo_consume.glb" },
    { key: "emo_dance", file: "/anim/ual/emo_dance.glb", keepHipsPos: true },
    { key: "emo_yes", file: "/anim/ual/emo_yes.glb" },
    { key: "emo_celebrate", file: "/anim/ual/emo_celebrate.glb" },
    // keepHipsPos (2026-08-02 "kneel emotes float"): these deploy with a
    // Manny-rebased pelvis journey (convert-ual hipsPos rows) — the hip DROP is
    // what puts the knees on the ground.
    { key: "emo_fixing", file: "/anim/ual/emo_fixing.glb", keepHipsPos: true },
    { key: "emo_chop", file: "/anim/ual/emo_chop.glb" },
    { key: "emo_farm_harvest", file: "/anim/ual/emo_farm_harvest.glb", keepHipsPos: true },
    { key: "emo_farm_plant", file: "/anim/ual/emo_farm_plant.glb", keepHipsPos: true },
    { key: "emo_farm_water", file: "/anim/ual/emo_farm_water.glb" },
    { key: "emo_farm_scatter", file: "/anim/ual/emo_farm_scatter.glb" },
    { key: "emo_lay", file: "/anim/ual/emo_lay.glb", once: true }, // bed sleep (holds lying)
    // Settlement profession work loops (2026-08-14): mining/hammering,
    // kneeling salvage, and the two Materia-channeling spell idles.
    { key: "emo_mine", file: "/anim/ual/emo_mine.glb" },
    { key: "emo_pickup", file: "/anim/ual/emo_pickup.glb", keepHipsPos: true },
    { key: "emo_channel", file: "/anim/ual/emo_channel.glb" },
    { key: "emo_channel2", file: "/anim/ual/emo_channel2.glb" },
    // THE VERTICAL GAME (UAL2, same offline-retargeted deploy). Player side: the LAUNCHER
    // (High Time — RMB focus + back + LMB, or hold LMB through the swing) and the aerial
    // string (airborne LMB fires on the press). NOTE the keys avoid both the "sd_attack"
    // ladder prefix (these run at 1.0×) and the retired "sd_air_" plunge-phase keys.
    { key: "sd_upper", file: "/anim/ual/sd_upper.glb", once: true },
    { key: "sd_air1", file: "/anim/ual/sd_air1.glb", once: true },
    { key: "sd_air2", file: "/anim/ual/sd_air2.glb", once: true },
    { key: "sd_gpound", file: "/anim/ual/sd_gpound.glb", once: true }, // the air-string slam
    { key: "sd_air_idle", file: "/anim/ual/sd_air_idle.glb" }, // floating stance between air hits
    // STINGER — the soft-lock gap-closer dash: PS Combo C4 (user pick, replacing the UAL
    // Sword_Dash), a lunging leap onto the target. UE-authored slow → plays at the attack
    // retime (SWORD_DASH_ANIM, avatar.ts); RM stripped at ingest, the sim homes the travel
    // and ends the dash on arrival, so the tail only shows on the longest closes.
    { key: "sd_dash", file: "/anim/manny/sd_dash.fbx", once: true },
    // AIR STINGER — the airborne gap-closer (PS CommonAttack/NormalAttack; RM stripped).
    { key: "sd_airdash", file: "/anim/manny/sd_airdash.fbx", once: true },
    // SHORT-close stinger — UAL2 Sword_Regular_C (user pick 2026-07-23, replacing PS Combo
    // C3): the spin windup is the approach, the late slash lands at arrival. UAL game-speed
    // → its natural rate is 1.0 (SD_DASH_NATURAL), unlike the 2.2× PS dashes.
    { key: "sd_shortdash", file: "/anim/ual/sd_shortdash.glb", once: true },
    // VICTIM lift set — what a LAUNCHED body plays (humanoid enemies via the streamed `lf`
    // posture; someday the player too). Loop clips loop; one-shots clamp (impact = lying).
    { key: "lift_up", file: "/anim/ual/lift_up.glb", once: true },
    { key: "lift_loop", file: "/anim/ual/lift_loop.glb" },
    { key: "lift_fall", file: "/anim/ual/lift_fall.glb" },
    { key: "lift_impact", file: "/anim/ual/lift_impact.glb", once: true },
    { key: "lift_hit_l", file: "/anim/ual/lift_hit_l.glb", once: true },
    { key: "lift_hit_r", file: "/anim/ual/lift_hit_r.glb", once: true },
    { key: "kip_up", file: "/anim/ual/kip_up.glb", once: true },
    { key: "hit_knockback", file: "/anim/ual/hit_knockback.glb", once: true },
    // PARRY (RMB tap in the sword stance) — the SSH pack's explicit parry swipe (Block_04_Parry),
    // played at SWORD_PARRY_ANIM so the table's sword_parry action covers it (avatar.swordParry).
    // UPPER-ONLY (user call 2026-07-22): the deflection rides OVER the locomotion legs —
    // tapping RMB must not stop movement (was a full-body root).
    { key: "sd_parry", file: "/anim/manny/sd_parry.glb", once: true, upperOnly: true },
    // HURT REACTIONS (SSH Hurt set, user call 2026-07-22 — Elden poise-break): HEAVY hits
    // interrupt the victim's attack and reel them (Hurt_Heavy); the directional lights are
    // the sd stance's non-interrupting flinch tier (avatar.flinch prefers them over hit_*).
    { key: "sd_hurt_heavy", file: "/anim/manny/sd_hurt_heavy.glb", once: true },
    { key: "sd_hurt_f", file: "/anim/manny/sd_hurt_f.glb", once: true },
    { key: "sd_hurt_b", file: "/anim/manny/sd_hurt_b.glb", once: true },
    { key: "sd_hurt_l", file: "/anim/manny/sd_hurt_l.glb", once: true },
    { key: "sd_hurt_r", file: "/anim/manny/sd_hurt_r.glb", once: true },
    // LOCKED STRAFE JUMP takeoffs (SSH Jump_Begin 4-way): Space while strafe-WALKING (no
    // Shift) jumps directionally; the roll lives on the Shift/jog tier now.
    { key: "sd_jump_f", file: "/anim/manny/sd_jump_f.glb", once: true },
    { key: "sd_jump_b", file: "/anim/manny/sd_jump_b.glb", once: true },
    { key: "sd_jump_l", file: "/anim/manny/sd_jump_l.glb", once: true },
    { key: "sd_jump_r", file: "/anim/manny/sd_jump_r.glb", once: true },
    // ENEMY STAGGER — the parry's prize: dizzy loop + recover, played by DRONE avatars via the
    // streamed `st` posture (the launch-state pattern; avatar.setStagger).
    { key: "st_loop", file: "/anim/manny/st_loop.glb" },
    { key: "st_end", file: "/anim/manny/st_end.glb", once: true },
    // RIPOSTE — the parry's punish (UAL2 Sword_Heavy_D via convert-ual): one big committed
    // strike at a STAGGERED enemy; fired by the action system in place of the combo step.
    { key: "sd_riposte", file: "/anim/ual/sd_riposte.glb", once: true },
    // BLOCK/DEFLECT — the blade's parry-connect reaction: the SSH Block_01 (user pick
    // 2026-07-22, replacing the UAL Sword_Block); avatar.parry() at SWORD_BLOCK_ANIM.
    // UPPER-ONLY like the swipe: a connected parry keeps the legs walking too.
    { key: "sd_block", file: "/anim/manny/sd_block.glb", once: true, upperOnly: true },
    // THE 2H GREATSWORD MODE (Valheim-style — docs/2h-combat-spec.md; combat-lab KeyG).
    // Essential S&S UE5/2H, native Manny, deployed TRIMMED to action length by
    // test/deploy-2h.mts (attack durations == COMBAT_ACTIONS gs_ rows at rate 1.0).
    { key: "gs_light1", file: "/anim/manny/gs_light1.glb", once: true },
    { key: "gs_light2", file: "/anim/manny/gs_light2.glb", once: true },
    { key: "gs_light3", file: "/anim/manny/gs_light3.glb", once: true }, // the overhead finisher
    { key: "gs_heavy", file: "/anim/manny/gs_heavy.glb", once: true },
    // The weapon-block set: raise → hold loop → lower; impact flinch + guard break play
    // over the hold (avatar.gsBlock* — the timed-block parry rides the raise window).
    { key: "gs_block_in", file: "/anim/manny/gs_block_in.glb", once: true },
    { key: "gs_block_loop", file: "/anim/manny/gs_block_loop.glb" },
    { key: "gs_block_out", file: "/anim/manny/gs_block_out.glb", once: true },
    { key: "gs_block_hit", file: "/anim/manny/gs_block_hit.glb", once: true },
    { key: "gs_break", file: "/anim/manny/gs_break.glb", once: true }, // guard break → the stagger
    // 2H stance idle (Idle_Combat) + THE OCTAGONAL LOCOMOTION (circling mode, user call
    // 2026-07-22): walk + jog strafes, free AND guarded (gwalk/gjog = the blade-up Guard
    // sets; jogs select on the run tier — Shift lifts combatSpeed past RUN_ANIM_MIN).
    // 6 dirs each; the two forward diagonals fall back to F via dirBlend.
    { key: "gs_idle", file: "/anim/manny/gs_idle.glb" },
    // Free (unlocked) 2H movement: the pack's own run/sprint cycles (Run_01/Run_02).
    { key: "gs_run", file: "/anim/manny/gs_run.glb", dir: "fwd" },
    { key: "gs_sprint", file: "/anim/manny/gs_sprint.glb", dir: "fwd" },
    // Z-STOW: back-carry sheathe/draw pair; the construct re-mounts mid-clip (avatar.gsStow).
    { key: "gs_sheathe", file: "/anim/manny/gs_sheathe.glb", once: true },
    // MASKED duplicates of the sheathe pair for the rifle Z-stow gesture: legs stripped, so
    // locomotion keeps running underneath (user call 2026-08-02 — "legs should continue").
    { key: "stow_sheathe", file: "/anim/manny/gs_sheathe.glb", once: true, upperOnly: true },
    { key: "stow_draw", file: "/anim/manny/gs_unsheathe.glb", once: true, upperOnly: true },
    { key: "gs_unsheathe", file: "/anim/manny/gs_unsheathe.glb", once: true },
    ...(["f", "b", "l", "r", "bl", "br"] as const).flatMap((d) => [
      { key: `gs_walk_${d}`, file: `/anim/manny/gs_walk_${d}.glb` },
      { key: `gs_jog_${d}`, file: `/anim/manny/gs_jog_${d}.glb` },
      { key: `gs_gwalk_${d}`, file: `/anim/manny/gs_gwalk_${d}.glb` },
      { key: `gs_gjog_${d}`, file: `/anim/manny/gs_gjog_${d}.glb` },
    ]),
    // 2H ROLLS (Space in circling mode — 2H/Roll): pure back rides the BL clip, the
    // deploy-sword precedent.
    ...(["f", "b", "l", "r", "bl", "br"] as const).map((d) => (
      { key: `gs_roll_${d}`, file: `/anim/manny/gs_roll_${d}.glb`, once: true }
    )),
    // DODGE one-shots (Space + direction while RMB-focused) — the UE5 SSH Dodge set (L/R from Dodge_01,
    // F/B from Dodge_03; full-body, ride the strike path → shown by the armed branch + carried by evade()).
    { key: "sd_dodge_f", file: "/anim/manny/sd_dodge_f.glb", once: true },
    { key: "sd_dodge_b", file: "/anim/manny/sd_dodge_b.glb", once: true },
    { key: "sd_dodge_l", file: "/anim/manny/sd_dodge_l.glb", once: true },
    { key: "sd_dodge_r", file: "/anim/manny/sd_dodge_r.glb", once: true },
    // CROUCH idle (the 8-way crouch strafe is added in the loop below, like the gun crouch set).
    { key: "sd_crouch_idle", file: "/anim/manny/sd_crouch_idle.glb", dir: "none" },
    // ROLL dodges — Space + direction WHILE CROUCHED plays a directional combat roll (swordDodge picks
    // sd_roll_* over sd_dodge_* when crouched). SSH Roll has no pure back, so sd_roll_b is the BL roll.
    // NOTE: the roll's ground-hugging crouch is PROCEDURAL (avatar rollDip — the body root
    // dips through the tumble). keepHipsPos was tried and reverted: these SSH FBX-sourced
    // clips carry UE-frame centimetre pelvis tracks WITH the forward root motion baked in,
    // which the native keepHipsPos path (built for rig-matched metre clips) mis-applies.
    // GROUND DODGE SET — the ORIGINAL SSH Roll family, restored in full (user calls
    // 2026-07-21: the UAL Roll/BackFlip/Dodge and the time-reversed back roll all got
    // tried and reverted; the SSH tumbles won). Back = the BL roll (no pure-back ships);
    // true back-diagonals below. The AIR dodge still uses the UAL Dodge_L/R separately.
    { key: "sd_roll_f", file: "/anim/manny/sd_roll_f.glb", once: true },
    { key: "sd_roll_b", file: "/anim/manny/sd_roll_b.glb", once: true },
    { key: "sd_roll_l", file: "/anim/manny/sd_roll_l.glb", once: true },
    { key: "sd_roll_r", file: "/anim/manny/sd_roll_r.glb", once: true },
    // Back-diagonal rolls (SSH Roll_BL/BR — true diagonals for the 6-way dodge pick).
    { key: "sd_roll_bl", file: "/anim/manny/sd_roll_bl.glb", once: true },
    { key: "sd_roll_br", file: "/anim/manny/sd_roll_br.glb", once: true },
    // (The 3-phase charge-plunge clips — sd_air_begin/loop/land — were RETIRED 2026-07-21
    //  with the air attack; files remain deployed for rollback.)
  ];
  for (const g of ["walk", "run", "crouch"] as const) {
    for (const d of DIRS8) out.push({ key: `sd_${g}_${d}`, file: `/anim/manny/sd_${g}_${d}.glb`, cyclicHips: g === 'run', closeLoop: g === 'run' });
  }
  return out;
}

export const CLIP_DEFS: readonly ClipDef[] = [
  // === NPC social set (test/deploy-npc.mjs → native-Manny "Relaxed" idles):
  // the conversation stance for settlement personas — npc_talk carries the
  // stand while talking, npc_gesture1..3 fire as occasional one-shots.
  // Extensible exactly like every player stance: add a ClipDef + deploy the
  // file; until it exists the state machine falls back via has(). ===
  { key: "npc_talk", file: "/anim/manny/npc_talk.fbx", dir: "none" },
  { key: "npc_gesture1", file: "/anim/manny/npc_gesture1.fbx", once: true },
  { key: "npc_gesture2", file: "/anim/manny/npc_gesture2.fbx", once: true },
  { key: "npc_gesture3", file: "/anim/manny/npc_gesture3.fbx", once: true },
  // === Unarmed / no-weapon — the body faces its velocity, so locomotion is
  // forward-only (idle/walk/run/sprint by speed). ===
  { key: "idle", file: "/anim/manny/idle.glb", dir: "none" },
  { key: "walk", file: "/anim/manny/walk.glb", dir: "fwd" },
  { key: "run", file: "/anim/manny/run.glb", dir: "fwd" },
  { key: "sprint", file: "/anim/manny/sprint.glb", dir: "fwd" },
  // OMNIDIRECTIONAL WALK (8-way loop blendspace) — the RMB-while-unarmed SLOW WALK stance: the body is
  // camera-locked so it strafes in any direction. Standard M_Neutral_Walk_Loop_* (native Manny), deployed
  // by test/deploy-omniwalk.mjs. NOT combat (see the UNARMED_COMBAT_ENABLED flag in main.ts).
  ...DIRS8.map((d): ClipDef => ({ key: `om_walk_${d}`, file: `/anim/manny/om_walk_${d}.glb` })),
  // Ready, empty-handed running: complete neutral directional clips. Splitting
  // pistol hips from a separately timed neutral torso loses the authored hip /
  // spine counter-rotation and makes the chest wobble. See deploy-omnirun.mts.
  ...DIRS8.map((d): ClipDef => ({ key: `om_run_${d}`, file: `/anim/manny/om_run_${d}.glb` })),
  { key: "jump", file: "/anim/ual/jump.glb", once: true }, // UAL Jump_Start (user pick 2026-07-21)
  // AIR DODGE — the UAL directional dodges (no rolling mid-air): full-body one-shots on an
  // airborne Space+direction, side picked from the input (avatar.airDodge).
  { key: "dodge_l", file: "/anim/ual/dodge_l.glb", once: true },
  { key: "dodge_r", file: "/anim/ual/dodge_r.glb", once: true },
  // DOUBLE JUMP one-shot (UAL2 DoubleJump — the standard flip, not the ninja set): played by
  // avatar.flagDoubleJump when the jetpack-gated second tap fires.
  { key: "djump", file: "/anim/ual/djump.glb", once: true },
  { key: "wall_run_l", file: "/anim/ual/wall_run_l.glb" },
  { key: "ledge_step", file: "/anim/ual/ledge_step.glb", once: true, keepHipsPos: true },
  { key: "ledge_climb", file: "/anim/ual/ledge_climb.glb", once: true, keepHipsPos: true },
  { key: "wall_run_r", file: "/anim/ual/wall_run_r.glb" },
  { key: "wall_jump_l", file: "/anim/ual/wall_jump_l.glb", once: true },
  { key: "wall_jump_r", file: "/anim/ual/wall_jump_r.glb", once: true },
  // NOTE: the unarmed falling clip ("fall") is intentionally REMOVED — its spread-eagle
  // pose was leaking in as a "leg splay" on flight transitions. Airborne-no-jet uses the
  // leap pose (jump/p_jump) instead. Re-add a fall clip here if a real falling pose is wanted.
  { key: "land", file: "/anim/manny/land.glb", once: true },
  { key: "crouch_idle", file: "/anim/manny/crouch_idle.glb", dir: "none" },
  { key: "crouch_walk", file: "/anim/manny/crouch_walk.glb", dir: "fwd" },
  // OCTAGONAL UNARMED CROUCH (8-way loop blendspace) — the empty-handed twin of the gun stances'
  // p_/r_crouch_<dir> set, same GASP Crouch_Loop_* pack with no weapon in hand (deployed by
  // test/deploy-crouch8.mjs). A crouched body is CAMERA-LOCKED (character.ts faces the heading while
  // crouched), so crouch movement strafes in any direction instead of only ever walking forward.
  // Bare keys — dirBlend takes an empty prefix and falls back crouch_<dir> -> crouch_f -> crouch_idle.
  ...DIRS8.map((d): ClipDef => ({ key: `crouch_${d}`, file: `/anim/manny/crouch_${d}.glb` })),
  // === Aim stances — the body faces the camera, so locomotion is an 8-way
  // directional BLENDSPACE (strafe). Pistol (p_) + rifle (r_). ===
  ...aimDefs("p"),
  ...aimDefs("r"),
  // === SWORD (melee) — armed-stance directional set (sd_), mirroring the gun sets. ===
  ...swordDefs(),
  // === FLIGHT — SUPERHERO FLIGHT set (Maya-authored Manny FBX, in /anim/flight/Anim_Fly_*; replaces the
  // jetpack flight clips, 2026-06-25). HOVER = the "Normal" fly (idle + fwd/back/left/right strafe); GLIDE
  // = the Boost directional family. Source filenames remain untouched. ===
  { key: "f_idle", file: "/anim/flight/Anim_Fly_Normal_Idle.glb" },           // hover idle
  { key: "f_fwd", file: "/anim/flight/Anim_Fly_Normal_Forwardmove.glb" },     // hover forward
  { key: "f_back", file: "/anim/flight/Anim_Fly_Normal_Backmove.glb" },       // hover back
  { key: "f_left", file: "/anim/flight/Anim_Fly_Normal_Leftmove.glb" },       // hover left
  { key: "f_right", file: "/anim/flight/Anim_Fly_Normal_Rightmove.glb" },     // hover right
  { key: "f_boost", file: "/anim/flight/Anim_Fly_Boost.glb" },               // GLIDE straight
  { key: "f_pitch_up", file: "/anim/flight/Anim_Fly_Boost_Turn_Up.glb" },   // pull-up and sustained ascent; arms trail beside the body
  // SUPER-JUMP charge: SuperHero "Take off 8" — a crouch WINDUP that HOLDS while Space is charged (not
  // moving), then its last bit (the launch) fires on release. Driven by the avatar super-jump state machine.
  { key: "f_superjump", file: "/anim/flight/Anim_SuperHero_Takeoff_08.glb", once: true },
  { key: "f_dive", file: "/anim/flight/Anim_Fly_Boost_Down.glb" },           // Ctrl-DIVE — the head-down plunge (main dive pose)
  // NOTE: the Superhero pack's Boost_Left/Boost_Right clips are named OPPOSITE to their on-screen
  // bank (a "Boost_Left" clip actually leans/turns right), so the files are crossed here. The turn
  // LOGIC is correct (+turnRate = right → f_bank_right, verified on the sphere); only the asset
  // labels are preserved as supplied. Runtime keys always describe the physical bank.
  // The separate _Turn assets below already have the correct physical names.
  { key: "f_bank_left", file: "/anim/flight/Anim_Fly_Boost_Right.glb" },    // glide turn left  (pack's "Right" clip banks left)
  { key: "f_bank_right", file: "/anim/flight/Anim_Fly_Boost_Left.glb" },    // glide turn right (pack's "Left" clip banks right)
  { key: "f_turn_left", file: "/anim/flight/Anim_Fly_Boost_Left_Turn.glb" },  // hard left turn (source direction is correct)
  { key: "f_turn_right", file: "/anim/flight/Anim_Fly_Boost_Right_Turn.glb" }, // hard right turn
  { key: "f_pitch_down", file: "/anim/flight/Anim_Fly_Boost_Turn_Down.glb" }, // glide steer down
  // Takeoff library clip + free third-jump entry transition (Normal_to_Boost).
  { key: "f_takeoff", file: "/anim/flight/Anim_Fly_Normal_Stand_to_Normal.glb", once: true },
  { key: "f_launch", file: "/anim/flight/Anim_Fly_Normal_to_Boost.glb", once: true },
  // Independent action clock: Ctrl can start this while the free launch fades.
  { key: "f_dive_start", file: "/anim/flight/Anim_Fly_Normal_to_Boost.glb", once: true },
  // Jetpack land transition kept as a fallback (no superhero flight-land wired yet).
  { key: "f_land", file: "/anim/flight/Anim_Jetpack_Land_02.glb", once: true },
  // DIVE touchdown flourish — the SuperHero flight-land (plays when a Ctrl-dive reaches the ground).
  // keepHipsPos: the clip IS a vertical journey (fly in ~1m up → drop to a 0.48 crouch at ~1.05s →
  // stand) — without the pelvis track the crouch floats at bind height with the feet in the air.
  { key: "f_dive_land", file: "/anim/flight/Anim_SuperHero_Flight_5_Land.glb", once: true, keepHipsPos: true },
  // === JUMP / FALL / LAND (M_Neutral jump set — Manny, in /anim/jump/, deployed by deploy-boostleap.mjs).
  // bl_loop is the universal "reasonable fall" loop (used by ALL non-jet falls). On the way DOWN the
  // avatar anticipates the touchdown and plays a land clip picked by along-ground SPEED (stand/walk/run/
  // sprint) and vertical IMPACT (light/heavy), crossfading bl_loop → land → locomotion. Left foot. ===
  { key: "bl_start", file: "/anim/jump/M_Neutral_Jump_F_Start_Sprint_Rfoot.glb", once: true }, // boost big-leap launch
  { key: "bl_loop", file: "/anim/jump/M_Neutral_Jump_Loop_Fall.glb" },                          // universal fall loop
  // Jump-START one-shots: a regular MOVING jump launches with the phase-matched start (run for a run,
  // sprint for a sprint), picked by along-ground speed — then crossfades into bl_loop. js_sprint shares
  // the boost-leap's launch clip (same file as bl_start).
  // Every gait's jump-start is the UAL jump now (user pick — one jump identity everywhere;
  // the gait-picked keys stay so the launch logic is untouched, they just share the file).
  { key: "js_stand", file: "/anim/ual/jump.glb", once: true },
  { key: "js_walk", file: "/anim/ual/jump.glb", once: true },
  { key: "js_run", file: "/anim/ual/jump.glb", once: true },
  { key: "js_sprint", file: "/anim/ual/jump.glb", once: true },
  { key: "fall_loop", file: "/anim/jump/M_Relaxed_Jump_Loop_Fall.glb" },                        // legacy fallback only
  { key: "land_stand_heavy", file: "/anim/jump/M_Neutral_Jump_F_Land_Stand_Heavy_Lfoot.glb", once: true },
  { key: "land_stand_light", file: "/anim/jump/M_Neutral_Jump_F_Land_Stand_Light_Lfoot.glb", once: true },
  { key: "land_walk_heavy", file: "/anim/jump/M_Neutral_Jump_F_Land_Walk_Heavy_Lfoot.glb", once: true },
  { key: "land_walk_light", file: "/anim/jump/M_Neutral_Jump_F_Land_Walk_Light_Lfoot.glb", once: true },
  { key: "land_run_heavy", file: "/anim/jump/M_Neutral_Jump_F_Land_Run_Heavy_Lfoot.glb", once: true },
  { key: "land_run_light", file: "/anim/jump/M_Neutral_Jump_F_Land_Run_Light_Lfoot.glb", once: true },
  { key: "land_sprint_heavy", file: "/anim/jump/M_Neutral_Jump_F_Land_Sprint_Heavy_Lfoot.glb", once: true },
  { key: "land_sprint_light", file: "/anim/jump/M_Neutral_Jump_F_Land_Sprint_Light_Lfoot.glb", once: true },
  // === UNARMED COMBAT — UPPER-BODY layer (leg tracks masked off; the legs keep running
  // locomotion underneath). RMB = fighting stance, backpedal = block, LMB = jab. The idle +
  // jab are MIXAMO clips, retargeted onto Manny + upper-body-masked so the legs stay mobile
  // (block/block-hit stay on the DHTH set). ===
  { key: "u_idle", file: "/anim/unarmed/ready_idle.glb", retarget: MIXAMO_TO_MANNY, upperOnly: true },
  // Lower half of the DHTH idle — the bladed fighting stance (left leg fwd, right back, ~55°
  // turned pelvis). Drives the legs+pelvis when standing so the torso sits on the correct hips
  // (matches the outfitter); pistol footwork takes over when moving.
  { key: "fist_stance", file: "/anim/unarmed/Anim_DHTH_Idle_RM.glb", lowerOnly: true },
  // BLOCK has no dedicated clip — backpedalling holds the guard idle and the blue shield GLOW signals
  // the block (see Avatar.updateCombatGlow / blockHit). The old u_block (kc_block.glb) + u_blockhit
  // (DHTH) cover clips were removed with that swap.
  // LMB jab — the Mixamo "Punching" clip, retargeted + upper-body-masked. Played from past its
  // wind-up + sped up (see Avatar.punch / JAB_START / JAB_SPEED) so the fist drives straight out.
  // Kept as FBX: this Mixamo clip exports WITHOUT a skin, so an fbx->glb round-trip
  // loses the source skeleton (GLTFLoader only rebuilds Bone nodes for skin joints)
  // and the retarget yields 0 bones. FBXLoader preserves the armature. (~0.4 MB.)
  // This Mixamo "Without Skin" export loads with its bone hierarchy in the authored rest pose.
  // Opt in explicitly so retarget.ts can use that rest without emitting the generic ambiguity warning.
  { key: "u_jab", file: "/anim/unarmed/punching.fbx", retarget: { ...MIXAMO_TO_MANNY, trustLiveRestWithoutSkin: true }, upperOnly: true, once: true },
  // === DHTH UNARMED REVAMP — the current combat model. Native Manny FBX (no retarget), deployed by
  // test/deploy-unarmed-dhth.mjs. The stance is a MASKED overlay: dh_guard (upper) over the kc_walk
  // strafe legs / dh_stance (still). PUNCHES are upper-body one-shots (legs keep moving — decoupled);
  // KICKS are full-body one-shots; BLOCK is a held full-body guard; parry/block-hit are reactions. ===
  { key: "dh_guard", file: "/anim/unarmed/dh_idle.glb", upperOnly: true },        // masked fight guard (upper)
  { key: "dh_stance", file: "/anim/unarmed/dh_idle.glb", lowerOnly: true },       // standing legs
  { key: "dh_punch_f", file: "/anim/unarmed/dh_punch_f.glb", upperOnly: true, once: true },
  { key: "dh_punch_b", file: "/anim/unarmed/dh_punch_b.glb", upperOnly: true, once: true },
  { key: "dh_punch_l", file: "/anim/unarmed/dh_punch_l.glb", upperOnly: true, once: true },
  { key: "dh_punch_r", file: "/anim/unarmed/dh_punch_r.glb", upperOnly: true, once: true },
  { key: "dh_kick_f", file: "/anim/unarmed/dh_kick_f.glb", once: true },          // full-body kicks
  { key: "dh_kick_b", file: "/anim/unarmed/dh_kick_b.glb", once: true },
  { key: "dh_kick_l", file: "/anim/unarmed/dh_kick_l.glb", once: true },
  { key: "dh_kick_r", file: "/anim/unarmed/dh_kick_r.glb", once: true },
  { key: "dh_block", file: "/anim/unarmed/dh_block.glb" },                        // held double-guard (loops)
  { key: "dh_blockhit", file: "/anim/unarmed/dh_blockhit.glb", once: true },      // block-impact flinch
  { key: "dh_parry", file: "/anim/unarmed/dh_parry.glb", once: true },            // parry snap
  // === KICKBOX locomotion legs (RETAINED): the 8-way strafe footwork + dodge rolls. The kickbox
  // STRIKE / idle / block / breaker clips below are LEGACY (superseded by the dh_* set) — kept loaded
  // but no longer played by the player path; the enemy brawler relay still references some by name. ===
  // === KICKBOX unarmed-combat locomotion (RMB stance): a FULL-BODY guard idle + 8-way movement
  // blendspace. GLB, converted from the FBX-7.7.0 Kickbox pack (test/convert-kickbox.mjs). These drive
  // the whole body in the combat stance (replacing the pistol-footwork legs); native Manny, no retarget. ===
  { key: "kc_idle", file: "/anim/kick/kc_idle.glb", dir: "none" },
  ...DIRS8.map((d): ClipDef => ({ key: `kc_walk_${d}`, file: `/anim/kick/kc_walk_${d}.glb` })),
  // The walk family's TRANSITIONS, one pair per octant: the pack authors every direction as
  // step-off → loop → settle, and the loops only read right when they are entered and left through
  // them (drop straight into a loop from a standstill and the first stride slides). The SHIELD
  // guard walks on these — see Avatar.updateGuardWalk.
  ...DIRS8.map((d): ClipDef => ({ key: `kc_walk_${d}_start`, file: `/anim/kick/kc_walk_${d}_start.glb`, once: true })),
  ...DIRS8.map((d): ClipDef => ({ key: `kc_walk_${d}_stop`, file: `/anim/kick/kc_walk_${d}_stop.glb`, once: true })),
  // Kickbox PARRY: the snap double-cover (anim_evasion_cover_double).
  { key: "kc_parry", file: "/anim/kick/kc_parry.glb", once: true },
  // STRIKES — full-body one-shots that ROOT the fighter (vs the masked u_jab). The P1 per-move
  // dispatch (Avatar.move, moves.ts) plays one per bind: jab / hook / low / mid / high kick.
  // Each is a once-clip; the combat layer adds the active one at full weight.
  { key: "kc_jab", file: "/anim/kick/kc_jab.glb", once: true },
  { key: "kc_cross", file: "/anim/kick/kc_cross.glb", once: true },
  { key: "kc_hook", file: "/anim/kick/kc_hook.glb", once: true },
  { key: "kc_upper", file: "/anim/kick/kc_upper.glb", once: true },
  { key: "kc_kick", file: "/anim/kick/kc_kick.glb", once: true },
  // MOVING strike variants — same strikes with baked forward/back ROOT TRAVEL (the *_move_F/_B clips
  // from the pack). Avatar.move() swaps to these when the player is advancing/retreating so the body
  // steps WITH the punch (no foot-slide) instead of sliding under an in-place clip; the in-place clips
  // above root the fighter when stationary. Only the jab has a back variant (the pack's coverage).
  { key: "kc_jab_f", file: "/anim/kick/kc_jab_f.glb", once: true },
  { key: "kc_jab_b", file: "/anim/kick/kc_jab_b.glb", once: true },
  { key: "kc_cross_f", file: "/anim/kick/kc_cross_f.glb", once: true },
  { key: "kc_hook_f", file: "/anim/kick/kc_hook_f.glb", once: true },
  { key: "kc_upper_f", file: "/anim/kick/kc_upper_f.glb", once: true },
  { key: "kc_kick_f", file: "/anim/kick/kc_kick_f.glb", once: true },
  // LEFT-leg roundhouse (Q kick); kc_kick above is the right-leg (E kick).
  { key: "kc_kick_l", file: "/anim/kick/kc_kick_l.glb", once: true },
  { key: "kc_kick_l_f", file: "/anim/kick/kc_kick_l_f.glb", once: true },
  // P1 moves: low + high roundhouse (in-place + forward advance), full-body once-clips.
  { key: "kc_lowk", file: "/anim/kick/kc_lowk.glb", once: true },
  { key: "kc_lowk_f", file: "/anim/kick/kc_lowk_f.glb", once: true },
  { key: "kc_highk", file: "/anim/kick/kc_highk.glb", once: true },
  { key: "kc_highk_f", file: "/anim/kick/kc_highk_f.glb", once: true },
  // EVADE — directional dodge rolls (full-body once-clips). dash direction picks the clip.
  { key: "kc_roll_f", file: "/anim/kick/kc_roll_f.glb", once: true },
  { key: "kc_roll_b", file: "/anim/kick/kc_roll_b.glb", once: true },
  { key: "kc_roll_l", file: "/anim/kick/kc_roll_l.glb", once: true },
  { key: "kc_roll_r", file: "/anim/kick/kc_roll_r.glb", once: true },
  // BLOCK-WHEN-HIT — directional cover flinches (one-shots), played only when a blocked hit lands.
  { key: "kc_blockhit_f", file: "/anim/kick/kc_blockhit_f.glb", once: true },
  { key: "kc_blockhit_b", file: "/anim/kick/kc_blockhit_b.glb", once: true },
  { key: "kc_blockhit_l", file: "/anim/kick/kc_blockhit_l.glb", once: true },
  { key: "kc_blockhit_r", file: "/anim/kick/kc_blockhit_r.glb", once: true },
  // IDLE BREAKERS — occasional full-body flourishes while standing guard (Avatar idle-breaker timer).
  { key: "kc_break1", file: "/anim/kick/kc_break1.glb", once: true },
  { key: "kc_break2", file: "/anim/kick/kc_break2.glb", once: true },
  // ARMED flight (gun drawn while hovering): aim-ready idle + banking aim-strafes.
  // Pistol (p) + rifle (r). The strafes are the *Shoot_Left/Right* clips — stable
  // banking aim poses (constant lean, seamless loop). NOT the Rifle_Evade clips,
  // which are short dodge maneuvers that fold the spine ~33° and lurch when looped.
  { key: "f_r_idle", file: "/anim/flight/Anim_Jetpack_Rifle_Idle.glb" },
  { key: "f_r_left", file: "/anim/flight/Anim_Jetpack_Rifle_Shoot_Left.glb", dir: "lateral" },
  { key: "f_r_right", file: "/anim/flight/Anim_Jetpack_Rifle_Shoot_Right.glb", dir: "lateral" },
  { key: "f_p_idle", file: "/anim/flight/Anim_Jetpack_Shoot_idle.glb" },
  { key: "f_p_left", file: "/anim/flight/Anim_Jetpack_Shoot_Left.glb", dir: "lateral" },
  { key: "f_p_right", file: "/anim/flight/Anim_Jetpack_Shoot_Right.glb", dir: "lateral" },
  // === SWIM — native UE5 Manny GLBs (bind directly, NO retarget), deployed by
  //     test/convert-swim.mjs. Whole-body locomotion that REPLACES the ground legs
  //     while swimming (like the flight clips do airborne): tread-water idle + 4-way
  //     surface swim + dive/rise. dir:"none" — the sim owns travel, not the root. ===
  // Float Left/Right come from a namespaced mGear rig; convert-swim.mjs strips the
  // `Manny_Mgear_Rig` prefix off the bones so they bind like the rest.
  { key: "sw_idle", file: "/anim/swim/sw_idle.glb", dir: "none" },
  { key: "sw_fwd", file: "/anim/swim/sw_fwd.glb", dir: "none" },
  { key: "sw_back", file: "/anim/swim/sw_back.glb", dir: "none" },
  { key: "sw_l", file: "/anim/swim/sw_l.glb", dir: "none" },
  { key: "sw_r", file: "/anim/swim/sw_r.glb", dir: "none" },
  { key: "sw_dive", file: "/anim/swim/sw_dive.glb", dir: "none" },
  { key: "sw_rise", file: "/anim/swim/sw_rise.glb", dir: "none" },
  // Fast stroke (front crawl) — Shift sprint-swim. Native Manny.
  { key: "sw_crawl", file: "/anim/swim/sw_crawl.glb", dir: "none" },
  { key: "sw_crawl_l", file: "/anim/swim/sw_crawl_l.glb", dir: "none" },
  { key: "sw_crawl_r", file: "/anim/swim/sw_crawl_r.glb", dir: "none" },
  // === MIXAMO clips (retargeted onto Manny on ingest) ===========================
  // To add one: (1) export from Mixamo as FBX — prefer **WITH SKIN** so the retarget reads
  //   the source BIND pose from the SkinnedMesh's inverse-bind matrices (unambiguous). A
  //   skin-less "Without Skin"/"Skeleton" export also works IF it retains the bind T-pose
  //   (Mixamo's usually do; some exporters leave the bones at frame 0 = wrong — a one-time
  //   console warning fires, eyeball the stance). Prefer an IN-PLACE clip (the sim owns
  //   translation; only pelvis XZ drift is read for stride-sync). Drop it in /anim/.  (2) Add a row
  //   here with `retarget: MIXAMO_TO_MANNY` — normalizeClip rebinds every mixamorig:
  //   bone onto the Manny rig automatically (auto-aligns the rest frame, rotation-only).
  //   (3) Reference its `key` from selectLocomotion (or solo it in the dev harness via
  //   Avatar.debugClip). Units (cm) and the Y-up/+Z facing are handled; no hand-tuning.
  // Example (uncomment + point at a real file):
  // { key: "wave", file: "/anim/mixamo/wave.glb", once: true, retarget: MIXAMO_TO_MANNY },
];

const ONCE_KEYS = new Set(CLIP_DEFS.filter((d) => d.once).map((d) => d.key));

/** Whether a clip key plays as a one-shot (LoopOnce + clampWhenFinished). */
export const isOnce = (key: string): boolean => ONCE_KEYS.has(key);

/** Max angle (rad) between the first and last keyframe quaternion across all
 *  bone tracks — the loop seam. */
function loopError(tracks: THREE.KeyframeTrack[]): number {
  const a = new THREE.Quaternion();
  const b = new THREE.Quaternion();
  let worst = 0;
  for (const t of tracks) {
    // Quaternion streams only — a keepHipsPos pelvis.position track read as
    // quats produced a phantom 173° seam (2026-08-02).
    if (!t.name.endsWith(".quaternion")) continue;
    const v = t.values as Float32Array;
    if (v.length < 8) continue;
    a.set(v[0], v[1], v[2], v[3]);
    b.set(v[v.length - 4], v[v.length - 3], v[v.length - 2], v[v.length - 1]);
    worst = Math.max(worst, a.angleTo(b));
  }
  return worst;
}

/**
 * Canonicalize one raw FBX clip onto the Manny rig: rename each bone's quaternion
 * track to the `.bones[<bone>].quaternion` path, drop position/scale (sim owns
 * translation). Pure (no I/O). Records the pelvis XZ drift for stride-sync.
 */
export function normalizeClip(
  src: THREE.Object3D,
  def: ClipDef,
  srcClip: THREE.AnimationClip = src.animations[0],
): NormalizedClip {
  const violations: string[] = [];
  const tracks: THREE.KeyframeTrack[] = [];
  let rootDriftX = 0;
  let rootDriftZ = 0;

  // FOREIGN skeleton: build a retargeter that rewrites each source bone's quaternion
  // onto its Manny bone (rotation-only). Absent => native Manny binding (pass-through).
  const retgt = def.retarget ? buildRetargeter(src, def.retarget) : null;
  // The bone whose .position carries hip travel (Manny "pelvis", or the source bone that
  // maps to it under a retarget) — read for stride-sync drift. Compared prefix-folded, like
  // the quaternion path, so a re-uploaded "mixamorig1:Hips.position" still resolves.
  const hipsBone = canonName(retgt ? retgt.hipsSourceName ?? "pelvis" : "pelvis");

  for (const t of srcClip.tracks) {
    const mq = /^(.+)\.quaternion$/.exec(t.name);
    if (mq) {
      // Retarget first: map this source bone's quats onto Manny (or drop it if unmapped).
      // Native clips skip this and keep their Manny bone name + raw values.
      let bone = mq[1];
      let values: ArrayLike<number> = t.values;
      if (retgt) {
        const c = retgt.convert(bone, t.values as Float32Array);
        if (!c) continue; // source bone not in the map → ride the rig's rest pose
        bone = c.target;
        values = c.values;
      } else {
        bone = NATIVE_BONE_ALIAS[bone] ?? bone; // e.g. UAL "Head" → rig "head"
      }
      // DROP the root track: the rig's root bone carries the Z-up -> Y-up + facing
      // conversion (mannyRig), which a clip's root.quaternion would overwrite. The
      // sim owns facing/translation, so the root never animates.
      if (bone === "root") continue;
      // UPPER-BODY MASK: drop pelvis/leg tracks so the clip layers only the torso/arms/head.
      if (def.upperOnly && LOWER_BODY.test(bone)) continue;
      // LOWER-BODY MASK: keep ONLY the pelvis/legs (the fighting-stance footwork + bladed hips).
      if (def.lowerOnly && !LOWER_BODY.test(bone)) continue;
      tracks.push(new THREE.QuaternionKeyframeTrack(`.bones[${bone}].quaternion`, t.times, values));
      continue;
    }
    // Record root (pelvis) translation, then drop position/scale tracks except
    // an explicit hip journey or cyclic weight-shift opt-in below.
    const mp = /^(.+)\.position$/.exec(t.name);
    if (mp && canonName(mp[1]) === hipsBone) {
      const v = t.values as Float32Array;
      // Net XZ travel, reconciled to the target frame via the retarget align (no-op for native
      // clips and for the common Y-up Mixamo case, but correct if the source up-axis differs).
      _drift.set(v[v.length - 3] - v[0], v[v.length - 2] - v[1], v[v.length - 1] - v[2]);
      if (retgt) _drift.applyQuaternion(retgt.align);
      rootDriftX = _drift.x;
      rootDriftZ = _drift.z;
      // keepHipsPos: preserve the authored hip JOURNEY (see ClipDef). Re-expressed as a delta off the
      // clip's own rest pelvis, applied about the RIG's pelvis bind — the two decompose hip height
      // differently (the rig carries a foot-offset in its root), so raw values must never bind raw.
      if (def.cyclicHips && !retgt) {
        const source = src.getObjectByName(mp[1]);
        const bind = MALE_RIG.find(b => b[0] === 'pelvis')!;
        if (source && Math.abs(source.position.z) > 1e-3) {
          const out = new Float32Array(v.length), count = v.length / 3;
          const scale = bind[4] / source.position.z;
          // Remove the small loop-end drift before centering. This keeps the
          // restored weight shift seamless even across directional crossfades.
          for (let axis = 0; axis < 3; axis++) {
            const drift = v[v.length - 3 + axis] - v[axis];
            let mean = 0;
            for (let i = 0; i < count; i++) {
              const phase = (t.times[i] - t.times[0]) / (t.times[count - 1] - t.times[0] || 1);
              out[i * 3 + axis] = v[i * 3 + axis] - drift * phase;
              mean += out[i * 3 + axis] / count;
            }
            for (let i = 0; i < count; i++)
              out[i * 3 + axis] = Number(bind[2 + axis]) + (out[i * 3 + axis] - mean) * scale * 1.15;
          }
          tracks.push(new THREE.VectorKeyframeTrack('.bones[pelvis].position', t.times, out));
        }
      } else if (def.keepHipsPos && !retgt) {
        const srcPelvis = src.getObjectByName(mp[1]);
        // The RIG's pelvis bind comes from the avatar's ACTUAL rest table (MALE_RIG — male
        // hip height), not the Manny table the clip was authored against.
        const bind = MALE_RIG.find((b) => b[0] === "pelvis");
        if (srcPelvis && bind) {
          const out = new Float32Array(v.length);
          for (let i = 0; i < v.length; i += 3) {
            out[i] = bind[2] + (v[i] - srcPelvis.position.x);
            out[i + 1] = bind[3] + (v[i + 1] - srcPelvis.position.y);
            out[i + 2] = bind[4] + (v[i + 2] - srcPelvis.position.z);
          }
          tracks.push(new THREE.VectorKeyframeTrack(`.bones[pelvis].position`, t.times, out));
        }
      } else if (def.keepHipsPos && retgt) {
        // RETARGETED hip journey: the source's pelvis delta (off its own rest), rotated into
        // world through the source root frame + align, SCALED by the hip-height ratio (the
        // skeletons' proportions differ), then written about OUR pelvis bind in root-bone-local
        // space (the root never animates — its rest rotation is the frame the track binds in).
        // Source scene and the realized rig share the world frame (both Y-up, rest-facing +Z —
        // measured for the UAL packs), so no extra facing reconciliation is needed beyond align.
        const srcPelvis = src.getObjectByName(mp[1]);
        if (srcPelvis && srcPelvis.parent) {
          const f = malePelvisFrame();
          const pq = srcPelvis.parent.getWorldQuaternion(new THREE.Quaternion());
          const srcHipY = srcPelvis.getWorldPosition(new THREE.Vector3()).y;
          if (srcHipY > 1e-3) {
            const s = f.hipY / srcHipY; // source units → rig units, hip-for-hip
            const rest = srcPelvis.position;
            const out = new Float32Array(v.length);
            const d = new THREE.Vector3();
            for (let i = 0; i < v.length; i += 3) {
              d.set(v[i] - rest.x, v[i + 1] - rest.y, v[i + 2] - rest.z)
                .applyQuaternion(pq) // source-local → source world
                .applyQuaternion(retgt.align) // → target world
                .multiplyScalar(s)
                .applyQuaternion(f.invRootQ); // → root-bone-local (the track's frame)
              out[i] = f.bind.x + d.x;
              out[i + 1] = f.bind.y + d.y;
              out[i + 2] = f.bind.z + d.z;
            }
            tracks.push(new THREE.VectorKeyframeTrack(`.bones[pelvis].position`, t.times, out));
          }
        }
      }
    }
  }

  const rawClip = new THREE.AnimationClip(def.key, srcClip.duration, tracks);
  const clip = def.closeLoop ? smoothLocomotionTorso(closeLocomotionLoop(rawClip)) : rawClip;

  const report: ClipReport = {
    animatedBones: tracks.length,
    rootDriftX,
    rootDriftZ,
    loopErrRad: loopError(clip.tracks),
  };

  // Masked clips legitimately carry fewer bones (one half stripped), so relax the floor.
  const minBones = def.upperOnly || def.lowerOnly ? 8 : MIN_ANIMATED_BONES;
  if (report.animatedBones < minBones) {
    violations.push(`only ${report.animatedBones} animated bones (< ${minBones})`);
  }
  if (!def.once && def.loop !== false && report.loopErrRad > LOOP_ERR_MAX) {
    violations.push(`loop seam ${(report.loopErrRad * 57.3).toFixed(0)}° (> ${(LOOP_ERR_MAX * 57.3).toFixed(0)}°)`);
  }

  return { key: def.key, clip, report, violations };
}

/** Cache of loaded anim-library GLBs keyed by base URL (no `#` fragment), so all
 *  ~120 clips of a multi-clip library (e.g. UAL1.glb) share ONE ~20 MB download. */
const previewGlbCache = new Map<string, Promise<GLTF>>();

/** Split a preview URL's trailing `#<clipIndex>` off the file URL. Absent or
 *  non-integer fragment → index 0 (preserves the single-clip FBX/GLB behavior). */
export function parseClipUrl(url: string): { base: string; index: number } {
  const h = url.lastIndexOf("#");
  if (h < 0) return { base: url, index: 0 };
  const idx = Number(url.slice(h + 1));
  return { base: url.slice(0, h), index: Number.isInteger(idx) && idx >= 0 ? idx : 0 };
}

/** GOD-MODE animation browser (dev): load + normalize ONE external anim-library clip
 *  by URL into a clip that binds the Manny rig. A `#<index>` fragment selects a clip
 *  inside a multi-clip GLB (the GLB is cached, so switching clips within a library is
 *  instant after the first load). Returns null on any failure (e.g. the dev-only
 *  /anim-library route is absent in production, or the index is out of range). */
export async function loadPreviewClip(url: string): Promise<THREE.AnimationClip | null> {
  try {
    const { base, index } = parseClipUrl(url);
    let src: THREE.Object3D;
    let srcClip: THREE.AnimationClip | undefined;
    if (/\.gl(b|tf)$/i.test(base)) {
      let p = previewGlbCache.get(base);
      if (!p) {
        p = new GLTFLoader().loadAsync(base).catch((e) => {
          previewGlbCache.delete(base); // don't cache a failed load — allow a retry
          throw e;
        });
        previewGlbCache.set(base, p);
      }
      const gltf = await p;
      src = gltf.scene;
      srcClip = gltf.animations[index];
    } else {
      src = await new FBXLoader().loadAsync(base);
      srcClip = src.animations[index];
    }
    if (!srcClip) {
      console.warn(`[anim] preview "${url}": no AnimationClip at index ${index}`);
      return null;
    }
    // universal_anims = the Quaternius UAL packs — a FOREIGN skeleton (UE-Manny NAMES but a
    // different rest/proportions), so they must be RETARGETED, not bound natively (native
    // binding would apply their raw locals onto our rig → mangled pose). keepHipsPos carries
    // the authored hip journey (62 of the 254 clips crouch/slide/lie/climb >25cm — rotations
    // alone leave the pelvis pinned at standing height and the pose reads contorted).
    const retarget = /\/universal_anims\//i.test(base) ? UAL_TO_MANNY : undefined;
    return normalizeClip(src, { key: url, file: url, retarget, keepHipsPos: !!retarget }, srcClip).clip;
  } catch (e) {
    console.warn(`[anim] preview "${url}" failed:`, e instanceof Error ? e.message : e);
    return null;
  }
}

let cache: Promise<{ clips: AvatarClips; reports: Map<string, ClipReport> }> | null = null;

let clipsLoaded = 0;
let clipsTotal = 0;

/** Load progress for the loader UI: settled clips / total. `total` is 0 until loadAll begins. */
export function getClipProgress(): { loaded: number; total: number } {
  return { loaded: clipsLoaded, total: clipsTotal };
}

/** Load + normalize all registry clips once; subsequent calls share them. */
export function getClips(): Promise<AvatarClips> {
  return getClipsAndReports().then((r) => r.clips);
}

/** Load + normalize + the per-clip reports (for diagnostics/tests in-browser). */
export function getClipsAndReports(): Promise<{ clips: AvatarClips; reports: Map<string, ClipReport> }> {
  cache ??= loadAll();
  return cache;
}

async function loadAll(): Promise<{ clips: AvatarClips; reports: Map<string, ClipReport> }> {
  const loader = new FBXLoader();
  const gltfLoader = new GLTFLoader();
  const clips: AvatarClips = {};
  const reports = new Map<string, ClipReport>();
  clipsTotal = CLIP_DEFS.length;
  clipsLoaded = 0;
  await Promise.all(
    CLIP_DEFS.map(async (def) => {
      try {
        // GLB/glTF path (e.g. the Kickbox pack, re-exported from FBX 7.7.0 which FBXLoader can't read
        // — see test/convert-kickbox.mjs): GLTFLoader yields clips on `gltf.animations`, not the scene,
        // so hang them on the scene Object3D for normalizeClip (which only reads `.animations[0]`).
        let src: THREE.Object3D;
        if (/\.gl(b|tf)$/i.test(def.file)) {
          const gltf = await gltfLoader.loadAsync(def.file);
          (gltf.scene as THREE.Object3D).animations = gltf.animations;
          src = gltf.scene;
        } else {
          src = await loader.loadAsync(def.file);
        }
        if (!src.animations[0]) throw new Error("no AnimationClip");
        const n = normalizeClip(src, def);
        if (n.violations.length) {
          console.warn(`[anim] "${def.key}" ingestion violations:`, n.violations.join("; "));
        }
        clips[def.key] = n.clip;
        reports.set(def.key, n.report);
      } catch (e) {
        // A clip whose FBX isn't present yet just stays absent; the state machine
        // falls back via has().
        console.warn(`[anim] ${def.file} not loaded — "${def.key}" absent:`, e instanceof Error ? e.message : e);
      } finally {
        clipsLoaded++;
      }
    }),
  );
  // Build marker (TEMP, for the flight-rebuild): confirms which build the tab is running.
  // If you don't see this exact line in DevTools, you're on a stale build — hard-reload.
  console.log(`%c[avatar] FLIGHT REBUILD "P1-P7+fluid" (crossfade handoffs, gradient climb/glide) — ${Object.keys(clips).length} clips loaded`, "color:#0ff");
  return { clips, reports };
}
